import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

// ============ Types ============

interface WizardProgressProps {
  currentStep: number;
  totalSteps: number;
  stepLabels: string[];
  className?: string;
}

// ============ Component ============

export function WizardProgress({
  currentStep,
  totalSteps,
  stepLabels,
  className,
}: WizardProgressProps) {
  return (
    <div className={cn('w-full', className)}>
      {/* Desktop: Full progress with labels */}
      <div className="hidden sm:block">
        {/* Progress bar */}
        <div className="relative h-2 bg-muted rounded-full overflow-hidden mb-4">
          <div
            className="absolute left-0 top-0 h-full bg-primary transition-all duration-300"
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          />
        </div>

        {/* Step labels */}
        <div className="flex justify-between">
          {stepLabels.map((label, index) => (
            <div
              key={index}
              className={cn(
                'flex items-center gap-2 text-sm',
                index <= currentStep ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              <div
                className={cn(
                  'size-6 rounded-full flex items-center justify-center text-xs font-medium',
                  index < currentStep
                    ? 'bg-primary text-primary-foreground'
                    : index === currentStep
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {index < currentStep ? (
                  <Check className="size-3" />
                ) : (
                  index + 1
                )}
              </div>
              <span className="hidden md:inline">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: Compact progress */}
      <div className="sm:hidden">
        {/* Step dots */}
        <div className="flex items-center justify-center gap-2 mb-3">
          {stepLabels.map((_, index) => (
            <div
              key={index}
              className={cn(
                'transition-all duration-300',
                index === currentStep
                  ? 'w-8 h-2 rounded-full bg-primary'
                  : index < currentStep
                  ? 'size-2 rounded-full bg-primary'
                  : 'size-2 rounded-full bg-muted'
              )}
            />
          ))}
        </div>

        {/* Current step label */}
        <p className="text-center text-sm">
          <span className="text-muted-foreground">Étape {currentStep + 1}/{totalSteps} · </span>
          <span className="font-medium">{stepLabels[currentStep]}</span>
        </p>
      </div>
    </div>
  );
}

// ============ Compact Progress ============

interface CompactProgressProps {
  currentStep: number;
  totalSteps: number;
  className?: string;
}

export function CompactProgress({
  currentStep,
  totalSteps,
  className,
}: CompactProgressProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {Array.from({ length: totalSteps }).map((_, index) => (
        <div
          key={index}
          className={cn(
            'h-1.5 flex-1 rounded-full transition-colors',
            index <= currentStep ? 'bg-primary' : 'bg-muted'
          )}
        />
      ))}
    </div>
  );
}
